/**
 * Allys Values Section Component
 * 
 * Premium styled values/features display with Allys design language
 */

'use client';

import { motion } from 'framer-motion';
import type { ValuesSectionProps } from '@routeroyal/theme-core';

/**
 * Allys Values Section - Clean grid layout for company values
 */
export function AllysValuesSection({
  title,
  subtitle,
  values,
  background = 'white',
  columns = 3
}: ValuesSectionProps) {
  const bgClass = background === 'gray' ? 'bg-gray-50' : 'bg-white';
  
  const gridCols = {
    2: 'lg:grid-cols-2',
    3: 'lg:grid-cols-3',
    4: 'lg:grid-cols-4'
  };
  
  return (
    <section className={`py-24 lg:py-32 ${bgClass} relative overflow-hidden`}>
      {/* Subtle background pattern */}
      {background === 'white' && (
        <div className="absolute inset-0 opacity-[0.015] bg-[radial-gradient(circle_at_50%_50%,#243462_1px,transparent_1px)] bg-[length:40px_40px]" />
      )}
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <div className="text-center mb-20">
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}
            className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-[#040813] mb-6 tracking-tight [font-family:'Inter',-apple-system,BlinkMacSystemFont,'Segoe_UI',system-ui,sans-serif]"
          >
            {title}
          </motion.h2>
          
          {subtitle && (
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.1, ease: [0.25, 0.46, 0.45, 0.94] }}
              className="text-lg lg:text-xl text-gray-600 max-w-2xl mx-auto font-light [font-family:'Inter',-apple-system,BlinkMacSystemFont,'Segoe_UI',system-ui,sans-serif]"
            >
              {subtitle}
            </motion.p>
          )}
        </div>
        
        {/* Values Grid */}
        <div className={`grid grid-cols-1 md:grid-cols-2 ${gridCols[columns]} gap-10 lg:gap-14`}>
          {values.map((value, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-100px' }}
              transition={{ duration: 0.8, delay: index * 0.1, ease: [0.25, 0.46, 0.45, 0.94] }}
              className="group"
            >
              {/* Icon */}
              {value.icon && (
                <div className="mb-6">
                  {typeof value.icon === 'string' ? (
                    value.icon.startsWith('http') ? (
                      // eslint-disable-next-line @next/next/no-img-element
                      <img 
                        src={value.icon} 
                        alt="" 
                        className="w-14 h-14 object-contain opacity-90"
                      />
                    ) : (
                      <div className="text-5xl opacity-90">
                        {value.icon}
                      </div>
                    )
                  ) : (
                    value.icon
                  )}
                </div>
              )}
              
              {/* Title */}
              <h3 className="text-2xl lg:text-3xl font-heading font-bold text-[#040813] mb-4 tracking-tight [font-family:'Inter',-apple-system,BlinkMacSystemFont,'Segoe_UI',system-ui,sans-serif]">
                {value.title}
              </h3>
              
              {/* Description */}
              <p className="text-base lg:text-lg text-gray-600 leading-relaxed font-light [font-family:'Inter',-apple-system,BlinkMacSystemFont,'Segoe_UI',system-ui,sans-serif]">
                {value.description}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

